import { GT_Project } from '../../types/projects'
import { GT_Remaining_Work } from '../../types/remaining-work'
import { GT_User } from '../../types/globals'
import { GT_Assembly_Date } from '../../types/dates'
import { Ctx } from '@app/context'
import { DateTime } from 'luxon'
import { Assembly_Controller, List_Manage_Assembly_Presenter } from '@app/controllers/assembly-controller'
import { List_Manage_Status_Workers_Presenter } from '@app/controllers/workers-controller'
import { bus } from '@app/core/bus'

export type Grid_Result = GT_Project|GT_Remaining_Work
export type Grid_Results = Grid_Result[]
export type Mapped_Results = Map<string, Grid_Results>

export abstract class Grid_List {
    abstract type: string
    name: string

    abstract supports_projects: boolean
    abstract supports_work: boolean
    abstract supports_dates: boolean
    abstract supports_week: boolean

    settings: {
        limit: string
        load: string
    } = gt_list_settings

    protected container: HTMLElement|null = null
    protected columns: Map<string, HTMLElement> = new Map()

    protected assembly_presenter?: List_Manage_Assembly_Presenter
    protected status_workers_presenter?: List_Manage_Status_Workers_Presenter

    protected constructor( ctx: Ctx ) {
        this.name = ctx.name

        this.set_container()
    }

    init_events() {
        bus.on( 'times:changed', () => void this.reload_results() )
    }

    protected set_container() {
        const element: HTMLElement|null = document.querySelector( '.grid-container' )

        if ( ! element ) {
            throw new Error( 'no container found' );
        }

        this.container = element
    }

    public get_container() {
        if ( ! this.container ) {
            throw new Error( 'no container found' );
        }
        return this.container;
    }

    public set_projects( posts: GT_Project[] = [] ) {
        gt_results.projects = posts
    }

    protected set_remaining_work( posts: GT_Remaining_Work[] = [] ) {
        gt_results.remaining_work = posts
    }

    public set_dates( posts: GT_Assembly_Date[] = [] ) {
        gt_results.dates = posts
    }

    public clear_results(): void {
        this.clear_projects()
        this.clear_work()

        this.hide_no_grid_results()
    }

    protected init_columns() {
        const html_columns: HTMLElement[] = Array.from( this.get_container().querySelectorAll( '[data-column]' ) )

        for ( const column of html_columns ) {
            console.log( 'column', column )

            const key: string|undefined = column.dataset.column

            if ( ! key ) {
                throw new Error( `${column} hat kein [data-column]` )
            }

            this.columns.set( key, column )
        }
    }

    render_columns( results: Mapped_Results ) {
        console.log( 'render results columns', results, Object.keys( results ) )

        for ( const key of results.keys() ) {
            const posts: Grid_Results|undefined = results.get( key )

            if ( ! posts ) {
                throw new Error( 'unbekannte Spalte' )
            }

            this.render_column( key, posts )
        }
    }

    protected abstract render_column( key: string, results: Grid_Results ): void

    protected clear_work(): void {
        console.log( 'clear work' )

        const results: HTMLElement[] = Array.from( this.get_container().querySelectorAll( '.remaining-work-item' ) )

        for ( const result of results ) {
            result.remove()
        }
    }

    protected clear_projects(): void {
        console.log( 'clear projects' )

        const results: HTMLElement[] = Array.from( this.get_container().querySelectorAll( '.project-item' ) )

        for ( const result of results ) {
            result.remove()
        }
    }

    protected show_no_grid_results() {
        const segment: HTMLElement|null = document.querySelector( '.no-grid-results' )
        if ( segment ) {
            segment.dataset.hidden = 'false'
        }
    }

    protected hide_no_grid_results() {
        const segment: HTMLElement|null = document.querySelector( '.no-grid-results' )
        if ( segment ) {
            segment.dataset.hidden = 'true'
        }
    }

    protected render_project?( key: string, project: GT_Project ): void
    protected render_remaining_work?( key: string, work: GT_Remaining_Work ): void

    protected sort_remaining_work_results( a: GT_Remaining_Work, b: GT_Remaining_Work ): 1|0|-1 {
        if ( 'priority' in a.properties && 'priority' in b.properties ) {
            return 0;
        }

        if ( 'priority' in a.properties ) {
            return -1;
        }

        if ( 'priority' in b.properties ) {
            return 1;
        }

        return 0
    }

    protected render_result( key: string, result: Grid_Result ) {
        console.log( 'render result', result.post_type )

        switch ( result.post_type ) {
            case 'project':
                this.render_project?.( key, result as GT_Project )
                break
            case 'remaining_work':
                this.render_remaining_work?.( key, result as GT_Remaining_Work )
                break
            default:
                throw Error( 'Post Type kann nicht gerendert werden' )
        }
    }

    protected create_project_schedule_title( post: HTMLDivElement, project: GT_Project ): HTMLAnchorElement {
        const a: HTMLAnchorElement = document.createElement( 'a' )

        let permalink: string = project.permalink

        if ( gt_permalinks[project.status.slug] ) {
            permalink = `${project.permalink}/?action-tab=${project.status.slug}`
        }

        a.classList.add( 'project-schedule-title' )
        a.href = permalink

        let title: string = ''

        if ( 'priority' in project.properties ) {
            post.classList.add( 'is-prioritized' )
            title += '<i class="star icon"></i>'
        }

        if ( 'remaining-work' in project.properties ) {
            post.classList.add( 'has-remaining-work' )
            title += '<i class="hourglass half icon"></i>'
        }

        if ( 'paused' in project.properties ) {
            post.classList.add( 'is-paused' )
            title += '<i class="pause icon"></i>'
        }

        title += ` ${project.title}`

        const schedule: HTMLDivElement = this.create_project_schedule( project )

        a.append( schedule )

        a.innerHTML += `<div class="title">${title}</div>`

        return a
    }

    protected create_project_schedule( project: GT_Project ): HTMLDivElement {
        const { deadlines, assembly, status } = project

        const div = document.createElement( 'div' )
        div.classList.add( 'schedule' )

        let deadline_sql

        switch ( status.slug ) {
            case 'assembly':
                deadline_sql = assembly.date || null
                break
            default:
                deadline_sql = deadlines?.[status.slug]?.date ?? null
                break
        }

        if ( ! deadline_sql ) {
            return div
        }

        const deadline: DateTime = DateTime.fromSQL( deadline_sql )
        const today: DateTime    = DateTime.now().startOf( 'day' )

        // console.log( 'sql', deadline_sql, 'status', status.slug )
        // console.log( 'deadline', deadline, 'today', today )

        if ( today.hasSame( deadline, 'day', { useLocaleWeeks: false } ) ) {
            div.innerHTML = '<span class="ui big circular label" data-status="ok"></span>'
        } else if ( deadline < today ) {
            div.innerHTML = '<span class="ui big circular label" data-status="bad"></span>'
        } else {
            div.innerHTML = '<span class="ui big circular label" data-status="good"></span>'
        }

        return div
    }

    protected create_project_status_workers( _post: HTMLDivElement, project: GT_Project ): HTMLDivElement {
        const div: HTMLDivElement = document.createElement( 'div' )

        div.classList.add( 'project-status-workers' )

        if ( project.status.slug === 'planning' ) {
            return div
        }

        const { status_workers } = project

        div.dataset.action = 'update-project-status-workers'

        if ( ! status_workers || ! status_workers[project.status.slug] || ! status_workers[project.status.slug].length ) {
            div.innerHTML = 'noch ohne zuständigen Mitarbeiter'
        } else {
            const workers: GT_User[] = []

            for ( const user of status_workers[project.status.slug] ) {
                const worker: GT_User|undefined = gt_workers.find( ( el: GT_User ): boolean => el.id === user.id )

                if ( worker ) {
                    workers.push( worker )
                }
            }

            for ( const worker of workers ) {
                div.innerHTML += `<div><i class="user alternate icon"></i> ${worker.name}</div>`
            }
        }

        return div
    }

    protected create_project_assembly_dates( _post: HTMLDivElement, project: GT_Project ): HTMLDivElement {
        const assembly = project.assembly

        const div: HTMLDivElement = document.createElement( 'div' )

        div.classList.add( 'project-assembly-dates' )

        div.dataset.action = 'update-project-assembly-date'

        const list: HTMLDivElement = document.createElement( 'div' )
        list.classList.add( 'badge-list' )

        if ( assembly.type === 'week' ) {
            list.innerHTML = `<span class="badge status secondary status-assembly" data-action="update-project-assembly-date">
                <i class="calendar alternate outline icon"></i> KW ${assembly.week}
            </span>`
        }

        if ( assembly.type === 'day' ) {
            list.innerHTML = `<span class="badge status status-assembly" data-action="update-project-assembly-date">
                <i class="calendar alternate outline icon"></i> ${format_date( assembly.date )}
            </span>`
        }

        if ( ! gt_results.dates.length ) {
            div.appendChild( list )
            return div
        }

        const additional:  GT_Assembly_Date[] = gt_results.dates.filter( ( el: GT_Assembly_Date ): boolean => el.project_post_id === project.post_id && el.category.slug === 'assembly' )

        // console.log( 'filtered', additional )

        additional.sort( ( a: GT_Assembly_Date, b: GT_Assembly_Date ): 1|0|-1 => Assembly_Controller.sort_assembly_dates( a.dates.start.date, b.dates.start.date ) )

        for ( const assembly of additional ) {
            list.innerHTML += `
			<span class="badge status additional-assembly status-assembly" data-action="update-project-additional-assembly-date">
				<i class="plus icon"></i> ${format_date( assembly.dates.start.date )}
			</span>
		`
        }

        div.appendChild( list )

        return div
    }

    protected create_project_remaining_work( _post: HTMLDivElement, project: GT_Project ): HTMLDivElement {
        const div: HTMLDivElement = document.createElement( 'div' )

        const properties = project.properties

        div.classList.add( 'project-remaining-work-status' )

        const total = gt_results.remaining_work.filter( el => el.project!.post_id === project.post_id )
        const open  = total.filter( el => el.status.slug !== 'completed' )

        if ( ! ( 'remaining-work' in properties ) ) {
            return div
        }

        if ( open.length === 1 ) {
            div.innerHTML = '<span class="badge status status-work secondary">Restarbeit vorhanden</span>'
        } else if ( open.length > 1 ) {
            div.innerHTML = `<span class="badge status status-work secondary">${open.length} Restarbeiten vorhanden</span>`
        } else {
            div.innerHTML = '<span class="badge status status-work secondary">Restarbeit(en) vorhanden</span>'
        }

        return div
    }

    protected create_remaining_work_title( _post: HTMLDivElement, work: GT_Remaining_Work ): HTMLAnchorElement {
        const a: HTMLAnchorElement = document.createElement( 'a' )

        const slug = ( work.status.slug === 'external' ? 'data' : work.status.slug )
        const project: GT_Project = work.project as GT_Project

        a.classList.add( 'remaining-work-title' )
        a.href = `${project.permalink}/?action-tab=${slug}`

        let title = ''

        if ( 'priority' in work.properties ) {
            title += '<i class="blue star icon"></i>'
        }

        if ( 'paused' in project.properties ) {
            title += '<i class="pause icon"></i>'
        }

        title += ` ${work.title}`

        a.innerHTML = `<div class="title">${title}</div>`

        return a
    }

    protected create_remaining_work_project_title( _post: HTMLDivElement, work: GT_Remaining_Work ): HTMLDivElement {
        const div = document.createElement( 'div' )
        const project = work.project as GT_Project

        div.classList.add( 'remaining-work-project-title' )

        div.innerHTML = `
		<div class="subtitle">
			<i class="project diagram icon"></i> ${project.title}
		</div>
	`

        return div
    }

    public abstract reload_results(): void
}
